import {
  useEffect, useCallback, useState, useMemo, useRef
} from 'react'
import Banner from '@/components/general/banner/Banner'
import { getAccessoryItem, getAccessoryDetail, countAccessory } from '@/service/accessory.api'
import styles from '@/styles/brand-detail.module.scss'
import BoxProduct from '@/components/box-product/BoxProduct'
import Input from '@/components/general/Input/Input'
import Select from '@/components/general/Select/Select'
import ReasonToBuy from '@/components/reason-to-buy/ReasonToBuy'
import { useSnackBar } from '@/context/app-context'
import useInfiniteLoad from '@/hooks/infiniteLoad'
import { addCommas } from '@/utils/formatter'
import Button from '@/components/general/Button/Button'
import NoData from '@/components/general/NoData/NoData'

export async function getServerSideProps() {
  try {
    const response = await getAccessoryDetail()
    const accessory = response.data.accessoryPage
    return {
      props: {
        accessoryImage: accessory?.banner?.url || ''
      }
    }
  } catch (e) {
    // console.log(e)
  }

  return {
    redirect: {
      destination: '/',
      permanent: false
    }
  }
}

export default function Accessories({ accessoryImage }) {
  // const router = useRouter()
  const snackbar = useSnackBar()
  const productListRef = useRef()
  const limit = 16
  const initialCurrentPage = 1
  const [countAll, setCountAll] = useState(0)
  const [currentPage, setCurrentPage] = useState(initialCurrentPage)
  const [loading, setLoading] = useState(true)
  const [filterChanged, setFilterChanged] = useState(true)
  // const [filterList, setFilterList] = useState({})
  const [filterData, setFilterData] = useState()
  const [searchFilter, setSearchFilter] = useState('')
  const [sortBy, setSortBy] = useState('createdAt:DESC')
  const [accessories, setAccessories] = useState([])

  const currentOffset = useMemo(() => (currentPage - 1) * limit, [currentPage])

  const maxPage = useMemo(() => Math.ceil(countAll / limit), [countAll])

  const fetchAccessories = useCallback(async (filter, offset, sort) => {
    setLoading(true)
    try {
      const response = await getAccessoryItem(filter, offset, limit, sort || undefined)
      const { accessories: list } = response.data
      if (offset === 0) {
        setAccessories(list)
      } else {
        setAccessories((prev) => ([...prev, ...list]))
      }
    } catch (e) {
      snackbar({
        isOpen: true,
        type: 'error',
        content: 'Cannot fetch product, please try again.'
      })
    } finally {
      setLoading(false)
    }
  }, [snackbar])

  const transformFilterToGraphqlQuery = (filter) => {
    const whereFilter = {}
    if (filter._or) {
      whereFilter._or = [...filter._or]
    }
    Object.keys(filter).forEach((key) => {
      if (key !== '_or') {
        if (Array.isArray(filter[key])) {
          if (filter[key].length) {
            whereFilter[`${key}_in`] = filter[key]
          }
        } else {
          whereFilter[key] = filter[key]
        }
      }
    })
    return whereFilter
  }

  useEffect(() => {
    if (filterChanged) {
      const query = transformFilterToGraphqlQuery(filterData || {})
      fetchAccessories(query, currentOffset, sortBy)
    }
  }, [fetchAccessories, filterData, currentOffset, sortBy, filterChanged])

  const fetchCountAll = useCallback(async (filter) => {
    const response = await countAccessory(filter)
    setCountAll(response.data.accessoriesCount)
  }, [])

  const resetPage = useCallback(() => {
    setAccessories([])
    setCurrentPage(initialCurrentPage)
  }, [initialCurrentPage])

  const doSearch = useCallback((keyword) => {
    const query = {
      _or: [
        {
          name_contains: keyword
        },
        {
          description_contains: keyword
        }
      ]
    }
    setFilterData((prev) => {
      const prevFilter = { ...prev }
      delete prevFilter.price_gte
      delete prevFilter.price_lte
      return { ...prevFilter, ...query }
    })
    setFilterChanged(true)
    resetPage()
  }, [resetPage])

  const handleSearchFilterKeyDown = (e) => {
    if (e.keyCode === 13 && !loading) {
      doSearch(searchFilter)
    }
  }

  useEffect(() => {
    if (filterChanged) {
      const query = transformFilterToGraphqlQuery(filterData || {})
      fetchCountAll(query)
    }
  }, [filterChanged, filterData, fetchCountAll])

  const loadMore = () => {
    if (currentPage < maxPage && !loading) {
      setCurrentPage((prev) => prev + 1)
    }
  }

  useInfiniteLoad(productListRef, loadMore)

  return (
    <>
      <Banner src={accessoryImage} text="Accessories" />
      <div className={`${styles.accessoryPage} max-width`}>
        <ReasonToBuy />
        <div className={styles.filterWrap}>
          <div className={styles.inputWithButton}>
            <Input
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Keyword"
              onKeyDown={handleSearchFilterKeyDown}
            />
            <Button
              onClick={() => doSearch(searchFilter)}
              stylingClass={styles.button}
            >
              Search
            </Button>
          </div>
          <Select
            stylingClass={styles.input}
            value={sortBy}
            onSelect={(item) => setSortBy(item.value)}
            items={[
              {
                text: 'Relevance',
                value: 'createdAt:DESC'
              },
              {
                text: 'Highest Price',
                value: 'price:DESC'
              },
              {
                text: 'Lowest Price',
                value: 'price:ASC'
              },
              {
                text: 'A-Z',
                value: 'name:ASC'
              },
              {
                text: 'Z-A',
                value: 'name:DESC'
              }
            ]}
          />
        </div>
        <div className={styles.watchList} ref={productListRef}>
          {
            accessories.length > 0
              ? accessories.map((accessory) => (
                <BoxProduct
                  key={accessory.id}
                  title={accessory.name}
              // subTitle={accessory.modelNumber}
                  image={accessory.image?.[0].url || ''}
              // category={accessory.category}
                  price={`฿ ${addCommas(accessory?.price?.toString() || '')}`}
                  url={`/accessories/${accessory.slug}`}
                />
              ))
              : (
                <NoData />
              )
          }
        </div>
      </div>
    </>
  )
}
